<script lang="ts" setup>
import { computed, onMounted, useTemplateRef } from 'vue'
import { storeToRefs } from 'pinia'
import { useDebounceFn } from '@vueuse/core'
import { translate } from '@/i18n/i18n.ts'

import { useEntryFormState } from '@/composables/entry-form/entry-form-state.composable.ts'
import { useEntryFormRules } from '@/composables/entry-form/entry-form-rules.composable.ts'
import { useEntryFormEventHandling } from '@/composables/entry-form/entry-form-event.composable.ts'
import { getSelectValue } from '@/utils/type.util.ts'

import { useFeatureFlagStore } from '@/stores/feature-flag.store.ts'
import { useUiStore } from '@/stores/ui.store.ts'

import type { SiteConnectVariant } from '@/libs/api/connect/connect.types.ts'
import { FLYOUT_CONTAINER_ID } from '@/ts/constants/constants.ts'
import { type FormResult } from '@/ts/types/form/form.types.ts'
import type { ProductOfferDto } from '@/ts/types/insurance.dto.ts'

import type { TariffState } from '@/views/variants/mini/mini-variant-tariff-state.composable'
import type { LValidationResult } from '@/libs/validation/components/LValidationForm.vue'
import { LFieldValidator, LValidationForm } from '@/libs/validation/components'
import {
  LIconButton,
  LSelect,
  LSelectCard,
  LSelectCardGroup,
  LTextfield
} from '@wgg.hyve/hyve-ui-library'

export type LSelectType = string | (string[] & string) | undefined // Workaround until the props from ui-library are public and accessible

export type EntryFormProps = {
  productOffer?: ProductOfferDto
  variant: SiteConnectVariant
}

export type EntryFormEmit = {
  (e: 'onFormUpdated', payload: FormResult<TariffState, Error>): void
  (e: 'onUpdate', value: boolean): void
}

const [formData] = defineModel<TariffState | undefined>('formData', {
  required: true
})
const [validationResult] = defineModel<LValidationResult>('validationResult', {
  required: true
})

const props = withDefaults(defineProps<EntryFormProps>(), {
  variant: 'default'
})

const emit = defineEmits<EntryFormEmit>()

const formRef = useTemplateRef<InstanceType<typeof LValidationForm>>('form')

const getPriceGroups = computed(() => props.productOffer?.price_groups?.['new'] || [])

const formState = useEntryFormState()
const {
  selectDeviceClass,
  inputPurchaseDate,
  inputPurchasePrice,
  formDataDto,
  deviceClassOptions,
  isFormFilled
} = formState

const uiStore = useUiStore()
const featureFlagsStore = useFeatureFlagStore()
const { flags } = storeToRefs(featureFlagsStore)

const { dateRules, priceRules, deviceClassRules } = useEntryFormRules()

const { validateForm } = useEntryFormEventHandling({
  emit,
  props,
  formRef,
  isFormFilled,
  formDataDto
})

const variantStyles = computed(() => {
  return {
    'entry-form-siteconnect': props.variant === 'default',
    'entry-form-mini': props.variant === 'mini'
  }
})

onMounted(() => {
  uiStore.setLoading(true)
  formState.initLinexoDefaultValues()

  setTimeout(() => {
    formData.value = formDataDto.value

    if (isFormFilled.value) {
      validateForm(true)
    }
  }, 1000)
})

const updateValue = (
  key: keyof TariffState,
  value: string | undefined,
  immediate: boolean = false
) => {
  emit('onUpdate', true)
  if (immediate) {
    updateFieldValue(key, value)
  } else {
    debouncedUpdate(key, value)
  }
}

const handleSelectedDeviceClassUpdate = async (
  selectedDeviceClass: string | string[],
  validate: () => Promise<boolean>
) => {
  await validate()
  updateValue('deviceClassId', getSelectValue(selectedDeviceClass as LSelectType))
}

const handlePurchaseDateUpdate = async (
  inputPurchaseDate: string | undefined,
  validate: () => Promise<boolean>
) => {
  await validate()
  updateValue('purchaseDate', inputPurchaseDate, true)
}

const updateFieldValue = (key: keyof TariffState, value: string | undefined) => {
  formData.value = {
    ...formData.value,
    [key]: value
  }
}

const debouncedUpdate = useDebounceFn((key: keyof TariffState, value: string | undefined) => {
  updateFieldValue(key, value)
}, 500)

function openPurchaseDateModal() {
  uiStore.setModal(
    'PurchaseDateModal',
    {
      header: {
        title: translate('modal_content.purchaseDate.title')
      },
      footer: {
        confirmationButton: {
          label: translate('modal_content.purchaseDate.to_dealer_search'),
          visible: true
        },
        closeButton: {
          label: translate('dialog.close_button_text'),
          visible: true
        }
      }
    },
    {
      onConfirm: () => {
        window.open(translate('checkout.target_url_dealersearch'), '_blank')
      }
    }
  )
}

const openPriceMatrixModal = () => {
  uiStore.setModal(
    'PriceMatrixModal',
    {
      header: {
        title: translate('modal_content.purchase_price.headline')
      },
      footer: {
        closeButton: {
          label: translate('dialog.close_button_text'),
          visible: true
        }
      }
    },
    {
      priceGroups: getPriceGroups.value
    }
  )
}

defineExpose({
  validateForm
})
</script>

<template>
  <div class="entry-form" :class="variantStyles">
    <div class="header-wrapper" v-if="variant === 'mini'">
      <h3 class="entry-form__header text-color">
        {{ translate('product.calculate_contribution') }}
      </h3>

      <p v-if="flags.get('design-v2')" class="entry-form__text text-color">
        {{ translate('product.calculate_contribution_description') }}
      </p>
    </div>

    <LValidationForm ref="form" v-model="validationResult" class="entry-form--spacings">
      <LFieldValidator
        v-slot="{ validate }"
        :model-value="selectDeviceClass"
        :rules="deviceClassRules"
        v-if="variant === 'mini'"
      >
        <LSelect
          id="deviceClassId"
          v-model="selectDeviceClass"
          :label="translate('form_fields.bike_type')"
          :multiple="false"
          :options="deviceClassOptions"
          :placeholder="translate('placeholder.select_placeholder')"
          :flyout-teleport-target="`#${FLYOUT_CONTAINER_ID}`"
          class="entry-form__select"
          @update:model-value="(value) => handleSelectedDeviceClassUpdate(value, validate)"
        />
      </LFieldValidator>

      <div v-if="variant === 'default'" class="entry-form__cards-wrapper">
        <div class="entry-form__cards-headline">
          {{ $t('content.device_select_title_bike') }}
        </div>
        <LSelectCardGroup
          id="entry-form-select-card-group"
          class="entry-form__select-card-group"
          background-variant="none"
          :flexible="false"
          :fixed-columns="2"
          @onItemSelected="
            (item) => updateValue('deviceClassId', deviceClassOptions[item.index]?.value)
          "
        >
          <LSelectCard
            v-for="(deviceClassOption, index) in deviceClassOptions"
            :id="`entry-form-select-card-${deviceClassOption.label}-${index}`"
            :key="`select-card-${deviceClassOption.label}-${index}`"
            :label="deviceClassOption.label"
            :size-mode="'flex'"
            background-variant="none"
          >
            <div class="entry-form__card-icon" v-html="deviceClassOption.icon" />
          </LSelectCard>
        </LSelectCardGroup>
      </div>

      <div class="entry-form__input-fields">
        <LFieldValidator
          v-slot="{ error, validate }"
          :model-value="inputPurchaseDate"
          :rules="dateRules"
        >
          <LTextfield
            id="purchaseDate"
            v-model="inputPurchaseDate"
            :errors="error"
            :label="translate('form_fields.device_purchase_date_day')"
            :placeholder="translate('placeholder.date_placeholder')"
            :disabled="!formData?.deviceClassId"
            class="entry-form__element"
            inputMode="numeric"
            mask="##.##.####"
            @blur="handlePurchaseDateUpdate(inputPurchaseDate, validate)"
          >
            <template #append-icon>
              <LIconButton
                icon="lnx-info"
                :aria-label="translate('form_fields.a11y.purchase_date_info')"
                @click.stop="openPurchaseDateModal"
              />
            </template>
          </LTextfield>
        </LFieldValidator>

        <LFieldValidator
          v-slot="{ error, validate }"
          :model-value="inputPurchasePrice"
          :rules="priceRules"
        >
          <LTextfield
            id="purchasePrice"
            v-model="inputPurchasePrice"
            :errors="error"
            :label="translate('form_fields.device_purchase_price_Bike')"
            :mask="{
              mask: '#',
              tokens: {
                '#': {
                  pattern: /[0-9]/,
                  repeated: true
                }
              }
            }"
            :placeholder="translate('placeholder.purchase_price_placeholder')"
            :disabled="!formData?.deviceClassId"
            class="entry-form__element"
            inputMode="numeric"
            @blur="validate()"
            @input="validate()"
            @update:modelValue="(value) => updateValue('purchasePrice', value)"
          >
            <template #append-icon>
              <LIconButton
                icon="lnx-info"
                :aria-label="translate('form_fields.a11y.purchase_price_info')"
                @click.stop="openPriceMatrixModal"
              />
            </template>
          </LTextfield>
        </LFieldValidator>
      </div>
    </LValidationForm>
    <div v-if="$slots.footer" class="entry-form__footer">
      <slot name="footer" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import 'entry-form';
</style>
